import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { pascalRow } from '../prob/counting';
import { createSampleSpaceGrid } from '../render/sample-space-grid';
import { createSpinner } from '../render/spinner';
import { createSvgEl } from '@learnforge/demo-render';
import { createVenn, type VennConfig, type VennRegionId } from '@learnforge/demo-render';
import type { ProbContext, ProbKey } from '../types';
import { createSliderRow, createToggleRow } from '@learnforge/demo-kit';
import { paramFormat } from '../ui/param-format';

/**
 * Indices (in the coin-flips grid encoding) of outcomes with exactly k heads.
 * The grid labels outcomes by setting bit b to 'T' if (i >> b) & 1 === 1, so
 * the count of heads is n − popcount(i).
 */
export function coinFlipIndicesWithKHeads(n: number, k: number): number[] {
  if (n < 0 || k < 0 || k > n) return [];
  const targetT = n - k;
  const out: number[] = [];
  const total = 2 ** n;
  for (let i = 0; i < total; i++) {
    let popcnt = 0;
    let x = i;
    while (x) {
      popcnt += x & 1;
      x >>>= 1;
    }
    if (popcnt === targetT) out.push(i);
  }
  return out;
}

const VENN_2_MAX_INTERSECTION = 12;
const VENN_3_MAX_INTERSECTION = 8;
const VENN_VIEW_CENTER = 240;

export function venn2Config(intersection: number): VennConfig {
  const i = Math.max(0, Math.min(VENN_2_MAX_INTERSECTION, Math.round(intersection)));
  const aOnly = VENN_2_MAX_INTERSECTION - i;
  const bOnly = VENN_2_MAX_INTERSECTION - i;
  const ab = i;
  const none = 30 - aOnly - bOnly - ab;
  // Slide circles closer together as intersection grows (200px apart → 30px apart).
  const d = 200 - i * ((200 - 30) / VENN_2_MAX_INTERSECTION);
  return {
    sets: [
      {
        id: 'A',
        labelKey: 'venn.set.A',
        cx: VENN_VIEW_CENTER - d / 2,
        cy: VENN_VIEW_CENTER,
        r: 95,
      },
      {
        id: 'B',
        labelKey: 'venn.set.B',
        cx: VENN_VIEW_CENTER + d / 2,
        cy: VENN_VIEW_CENTER,
        r: 95,
      },
    ],
    universe: 30,
    regionCounts: { A: aOnly, B: bOnly, AB: ab, none },
  };
}

export function venn3Config(intersection: number): VennConfig {
  const i = Math.max(0, Math.min(VENN_3_MAX_INTERSECTION, Math.round(intersection)));
  const only = VENN_3_MAX_INTERSECTION - i;
  const pair = i;
  const triple = Math.round(i / 2);
  const inside = 3 * only + 3 * pair + triple;
  const none = 30 - inside;
  // Triangle radius from center shrinks as intersection grows.
  const R = 95 - i * ((95 - 45) / VENN_3_MAX_INTERSECTION);
  const cosA = Math.cos(Math.PI / 6); // 30° from horizontal
  const sinA = Math.sin(Math.PI / 6);
  return {
    sets: [
      {
        id: 'A',
        labelKey: 'venn.set.A',
        cx: VENN_VIEW_CENTER - R * cosA,
        cy: VENN_VIEW_CENTER - R * sinA,
        r: 95,
      },
      {
        id: 'B',
        labelKey: 'venn.set.B',
        cx: VENN_VIEW_CENTER + R * cosA,
        cy: VENN_VIEW_CENTER - R * sinA,
        r: 95,
      },
      {
        id: 'C',
        labelKey: 'venn.set.C',
        cx: VENN_VIEW_CENTER,
        cy: VENN_VIEW_CENTER + R,
        r: 95,
      },
    ],
    universe: 30,
    regionCounts: { A: only, B: only, C: only, AB: pair, AC: pair, BC: pair, ABC: triple, none },
  };
}

export type SubMode =
  | '2d6-grid'
  | '2d6-sum'
  | '1d6'
  | 'coin-flips'
  | 'card-suit'
  | 'spinner'
  | 'venn-2set'
  | 'venn-3set';

export interface SampleSpaceModeOpts {
  flips?: number;
}

export function enumerateOutcomes(
  mode: SubMode,
  opts: SampleSpaceModeOpts = {},
): readonly unknown[] {
  switch (mode) {
    case '2d6-grid':
      return Array.from({ length: 36 }, (_, i) => [Math.floor(i / 6) + 1, (i % 6) + 1]);
    case '2d6-sum':
      return Array.from({ length: 11 }, (_, i) => i + 2);
    case '1d6':
      return [1, 2, 3, 4, 5, 6];
    case 'coin-flips':
      return Array.from({ length: 2 ** (opts.flips ?? 2) }, (_, i) => i);
    case 'card-suit':
      return ['♠', '♥', '♦', '♣'];
    case 'spinner':
      return ['sector-1', 'sector-2', 'sector-3'];
    case 'venn-2set':
      return ['A-only', 'B-only', 'A∩B', 'neither'];
    case 'venn-3set':
      return ['A-only', 'B-only', 'C-only', 'A∩B', 'A∩C', 'B∩C', 'A∩B∩C', 'neither'];
  }
}

export function eventCardinality(eventIndices: readonly number[]): number {
  return eventIndices.length;
}

export type SetOp = 'union' | 'intersect' | 'complement';

export function applySetOp(
  a: readonly number[],
  b: readonly number[],
  op: SetOp,
  universe?: number,
): number[] {
  switch (op) {
    case 'union': {
      const set = new Set<number>(a);
      for (const x of b) set.add(x);
      return [...set];
    }
    case 'intersect': {
      const bSet = new Set<number>(b);
      return a.filter((x) => bSet.has(x));
    }
    case 'complement': {
      if (universe === undefined) return [];
      const aSet = new Set<number>(a);
      const result: number[] = [];
      for (let i = 0; i < universe; i++) if (!aSet.has(i)) result.push(i);
      return result;
    }
  }
}

// ============================================================================
// State
// ============================================================================

interface State {
  mode: SubMode;
  flips: number;
  selected: number[];
  pascalOverlay: boolean;
  venn2Intersection: number;
  venn3Intersection: number;
  venn2Selected: VennRegionId[];
  venn3Selected: VennRegionId[];
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): CustomExperimentSpec<State, ProbKey, ProbContext> {
  return {
    labelKey: 'experiment.sample-space-lab.label',
    demoId: 'probability',
    advancedKey: 'sample-space-lab',

    initialState(): State {
      return {
        mode: '2d6-grid',
        flips: 3,
        selected: [],
        pascalOverlay: false,
        venn2Intersection: 6,
        venn3Intersection: 3,
        venn2Selected: [],
        venn3Selected: [],
      };
    },

    createRender(ctx, host, controls) {
      // ── Stage SVG (persists for the lifetime of this render handle) ──
      const stageSvg = createSvgEl('svg', { viewBox: '0 0 600 600', class: 'pb-sslab-svg' });
      host.appendChild(stageSvg);
      const stageGroup = createSvgEl('g');
      stageSvg.appendChild(stageGroup);

      // ── Per-submode handles (closure vars; null when not active) ──
      let gridHandle: ReturnType<typeof createSampleSpaceGrid> | null = null;
      let spinnerHandle: ReturnType<typeof createSpinner> | null = null;
      let vennHandle: ReturnType<typeof createVenn> | null = null;
      let pascalGroup: SVGGElement | null = null;

      // ── Panel elements created once ──
      const submodeLabel = document.createElement('label');
      submodeLabel.textContent = ctx.i18n.t('param.submode.label');
      const modeSelect = document.createElement('select');
      const MODES: SubMode[] = [
        '2d6-grid',
        '2d6-sum',
        '1d6',
        'coin-flips',
        'card-suit',
        'spinner',
        'venn-2set',
        'venn-3set',
      ];
      for (const m of MODES) {
        const opt = document.createElement('option');
        opt.value = m;
        opt.textContent = ctx.i18n.t(`submode.sample-space-lab.${m}.label` as never);
        modeSelect.appendChild(opt);
      }
      modeSelect.addEventListener('change', () => {
        controls.setState((s) => ({ ...s, mode: modeSelect.value as SubMode, selected: [] }));
      });
      const submodeRow = document.createElement('div');
      submodeRow.className = 'pb-dropdown-row';
      submodeRow.append(submodeLabel, modeSelect);
      ctx.panel.appendChild(submodeRow);

      // Conditional panel section (flips slider + pascal checkbox, or intersection
      // slider) — re-created when the mode changes to avoid stale element refs.
      let conditionalSection: HTMLElement[] = [];

      // Readout div — appended once; stays in panel throughout lifetime.
      const readout = document.createElement('div');
      readout.id = 'sslab-readout';
      readout.className = 'pb-sslab-readout';
      ctx.panel.appendChild(readout);

      // ── Helpers ──

      function currentVennConfig(state: State): VennConfig | null {
        if (state.mode === 'venn-2set') return venn2Config(state.venn2Intersection);
        if (state.mode === 'venn-3set') return venn3Config(state.venn3Intersection);
        return null;
      }

      function currentVennSelection(state: State): VennRegionId[] {
        return state.mode === 'venn-2set' ? state.venn2Selected : state.venn3Selected;
      }

      function updateReadout(state: State): void {
        let universe: number;
        let cardinality: number;
        if (state.mode === 'venn-2set' || state.mode === 'venn-3set') {
          const config = currentVennConfig(state)!;
          universe = Object.values(config.regionCounts).reduce((s, n) => s + n, 0);
          const selectedRegions = currentVennSelection(state);
          cardinality = selectedRegions.reduce((s, id) => s + (config.regionCounts[id] ?? 0), 0);
        } else {
          universe = enumerateOutcomes(state.mode, { flips: state.flips }).length;
          cardinality = eventCardinality(state.selected);
        }
        const p = universe > 0 ? cardinality / universe : 0;
        readout.textContent = `${ctx.i18n.t('stat.event-probability')}: ${p.toFixed(3)} (|E|=${cardinality}, |Ω|=${universe})`;
      }

      // ── Pascal row renderer (inline SVG chips into stageGroup) ──
      function renderPascalRow(state: State): void {
        if (pascalGroup) pascalGroup.remove();
        pascalGroup = null;
        if (!(state.pascalOverlay && state.mode === 'coin-flips')) return;

        const n = state.flips;
        const row = pascalRow(n);
        const cols = Math.min(8, 2 ** n);
        const bandWidth = cols * 50;
        const bandX0 = 10;
        const chipW = Math.min(60, bandWidth / (n + 1) - 6);
        const chipH = 46;
        const cy = 28;
        const selectedSet = new Set(state.selected);

        const group = createSvgEl('g', { class: 'pb-pascal-row' });
        for (let k = 0; k <= n; k++) {
          const count = row[k]!;
          const headIndices = coinFlipIndicesWithKHeads(n, k);
          const allSelected =
            headIndices.length > 0 && headIndices.every((i) => selectedSet.has(i));
          const cx = bandX0 + ((k + 0.5) * bandWidth) / (n + 1);
          const chip = createSvgEl('g', {
            class: `pb-pascal-chip ${allSelected ? 'pb-pascal-chip-selected' : ''}`.trim(),
            'data-k': String(k),
            role: 'button',
            tabindex: 0,
          });
          chip.appendChild(
            createSvgEl('rect', {
              class: 'pb-pascal-chip-bg',
              x: cx - chipW / 2,
              y: cy - chipH / 2,
              width: chipW,
              height: chipH,
              rx: 6,
            }),
          );
          const countText = createSvgEl('text', {
            class: 'pb-pascal-chip-count',
            x: cx,
            y: cy - 5,
            'text-anchor': 'middle',
            'dominant-baseline': 'central',
          });
          countText.textContent = String(count);
          chip.appendChild(countText);
          const caption = createSvgEl('text', {
            class: 'pb-pascal-chip-caption',
            x: cx,
            y: cy + 12,
            'text-anchor': 'middle',
            'dominant-baseline': 'central',
          });
          caption.textContent = `${k} H`;
          chip.appendChild(caption);
          const toggle = (): void => {
            controls.setState((s) => {
              let next: number[];
              if (allSelected) {
                const toRemove = new Set(headIndices);
                next = s.selected.filter((i) => !toRemove.has(i));
              } else {
                const set = new Set(s.selected);
                for (const i of headIndices) set.add(i);
                next = [...set];
              }
              return { ...s, selected: next };
            });
          };
          chip.addEventListener('click', toggle);
          chip.addEventListener('keydown', (ev) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              ev.preventDefault();
              toggle();
            }
          });
          group.appendChild(chip);
        }
        stageGroup.appendChild(group);
        pascalGroup = group;
      }

      // ── Submode lifecycle helpers ──

      function destroyCurrentSubmode(): void {
        gridHandle?.destroy();
        spinnerHandle?.destroy();
        vennHandle?.destroy();
        gridHandle = null;
        spinnerHandle = null;
        vennHandle = null;
        if (pascalGroup) {
          pascalGroup.remove();
          pascalGroup = null;
        }
        stageGroup.replaceChildren();
      }

      function buildSubmode(state: State): void {
        switch (state.mode) {
          case '2d6-grid':
          case '2d6-sum':
          case '1d6':
          case 'card-suit':
          case 'coin-flips': {
            const showPascal = state.pascalOverlay && state.mode === 'coin-flips';
            if (showPascal) renderPascalRow(state);
            const gridContainer = createSvgEl('g');
            if (showPascal) gridContainer.setAttribute('transform', 'translate(0, 80)');
            stageGroup.appendChild(gridContainer);

            const gridOpts: Parameters<typeof createSampleSpaceGrid>[1] = {
              kind: state.mode,
              selected: state.selected,
              onToggle: (i) => {
                controls.setState((s) => {
                  const next = s.selected.includes(i)
                    ? s.selected.filter((x) => x !== i)
                    : [...s.selected, i];
                  return { ...s, selected: next };
                });
              },
              i18n: ctx.i18n,
            };
            if (state.mode === 'coin-flips') {
              gridOpts.flips = state.flips;
            }
            gridHandle = createSampleSpaceGrid(gridContainer, gridOpts);
            break;
          }
          case 'spinner': {
            const caption = ctx.i18n.t('spinner.legend.sector');
            spinnerHandle = createSpinner(stageGroup, {
              partitions: [0.4, 0.3, 0.3],
              cx: 220,
              cy: 240,
              r: 160,
              labels: [`${caption} 1`, `${caption} 2`, `${caption} 3`],
              legendCaption: caption,
              onChange: () => undefined,
            });
            break;
          }
          case 'venn-2set':
          case 'venn-3set': {
            const config = currentVennConfig(state)!;
            vennHandle = createVenn({
              container: ctx.root,
              i18n: ctx.i18n,
              initial: config,
              selected: currentVennSelection(state),
              onSelectionChange: (next) => {
                controls.setState((s) => {
                  if (s.mode === 'venn-2set') return { ...s, venn2Selected: [...next] };
                  if (s.mode === 'venn-3set') return { ...s, venn3Selected: [...next] };
                  return s;
                });
              },
            });
            break;
          }
        }
        if (state.pascalOverlay && state.mode === 'coin-flips') {
          void pascalRow(state.flips); // reserved hook
        }
      }

      // ── Conditional panel section ──

      function buildConditionalPanel(state: State): void {
        // Remove previous conditional elements.
        for (const el of conditionalSection) el.remove();
        conditionalSection = [];

        // Insert before readout (which is always the last child).
        const insertBefore = readout;

        if (state.mode === 'coin-flips') {
          const sliderHandle = createSliderRow({
            id: 'sslab-flips',
            label: ctx.i18n.t('param.flips.label'),
            i18n: ctx.i18n,
            min: 1,
            max: 10,
            step: 1,
            value: state.flips,
            format: paramFormat,
            onChange: (v) => {
              controls.setState((s) => ({ ...s, flips: v }));
            },
          });
          // Insert before readout (createSliderRow does not append on its own).
          ctx.panel.insertBefore(sliderHandle.element, insertBefore);
          conditionalSection.push(sliderHandle.element);

          const pascalToggle = createToggleRow({
            labelKey: 'param.pascal-overlay.label',
            i18n: ctx.i18n,
            checked: state.pascalOverlay,
            onChange: (checked) => {
              controls.setState((s) => ({ ...s, pascalOverlay: checked }));
            },
          });
          pascalToggle.input.id = 'pascal-overlay';
          ctx.panel.insertBefore(pascalToggle.element, insertBefore);
          conditionalSection.push(pascalToggle.element);
        }

        if (state.mode === 'venn-2set' || state.mode === 'venn-3set') {
          const sliderHandle = createSliderRow({
            id: 'sslab-intersection',
            label: ctx.i18n.t('param.intersection.label'),
            i18n: ctx.i18n,
            min: 0,
            max: state.mode === 'venn-2set' ? VENN_2_MAX_INTERSECTION : VENN_3_MAX_INTERSECTION,
            step: 1,
            value: state.mode === 'venn-2set' ? state.venn2Intersection : state.venn3Intersection,
            format: paramFormat,
            onChange: (v) => {
              controls.setState((s) => {
                if (s.mode === 'venn-2set') return { ...s, venn2Intersection: v };
                if (s.mode === 'venn-3set') return { ...s, venn3Intersection: v };
                return s;
              });
            },
          });
          ctx.panel.insertBefore(sliderHandle.element, insertBefore);
          conditionalSection.push(sliderHandle.element);
        }
      }

      // ── Tracked state for reconciliation ──
      let lastMode: SubMode | undefined;

      // ── RenderHandle ──
      const update = (state: State): void => {
        const modeChanged = state.mode !== lastMode;

        // Reconcile mode select.
        modeSelect.value = state.mode;

        if (modeChanged) {
          // Destroy previous submode's handles and clear the stage.
          destroyCurrentSubmode();
          // Build new submode.
          buildSubmode(state);
          // Rebuild the conditional panel section.
          buildConditionalPanel(state);
          lastMode = state.mode;
        } else {
          // Reconcile the existing submode without rebuilding.
          if (gridHandle) {
            gridHandle.setSelected(state.selected);
            // If coin-flips, reconcile Pascal row.
            if (state.mode === 'coin-flips') {
              // Rebuild Pascal row (cheap SVG op).
              if (pascalGroup) {
                pascalGroup.remove();
                pascalGroup = null;
              }
              if (state.pascalOverlay) {
                renderPascalRow(state);
              }
            }
          }
          if (vennHandle) {
            const config = currentVennConfig(state)!;
            vennHandle.update(config);
            vennHandle.setSelection(currentVennSelection(state));
          }

          // Do NOT rebuild the conditional panel here — the sliders are user-owned
          // and rebuilding every tick causes DOM churn, focus loss mid-drag, and
          // flicker. The panel was already built correctly on the most recent mode
          // change. Slider values are live (they drive state via onInput) and do
          // not need to be re-synced from state on non-mode-change updates.
        }

        updateReadout(state);
      };

      return {
        update,
        dispose() {
          // Destroy whichever submode handles currently exist.
          gridHandle?.destroy();
          spinnerHandle?.destroy();
          vennHandle?.destroy();
          gridHandle = null;
          spinnerHandle = null;
          vennHandle = null;
          // NOTE: do NOT replaceChildren() on ctx.root or ctx.panel here;
          // the harness does that after dispose() runs.
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// Keep registry wiring: index.ts imports mountSampleSpaceLab by name.
export const mountSampleSpaceLab = experiment.mount.bind(experiment);
